import { useCallback, useEffect, useMemo, useReducer } from 'react';

import type { UserSessionContextState } from '../contexts/UserSessionContext';
import UserSessionContext from '../contexts/UserSessionContext';
import { useMsal } from '@azure/msal-react';
import { usePrevious } from 'react-use';
import { sessionStorageService } from '../utils/helpers/sessionStorageService';
import { getAuthInfo } from '../hooks/fetching/useUserRolesFetch';
import type { AccountInfo } from '@azure/msal-browser';

interface UserSessionProviderProps {
  children: React.ReactNode;
}

type ProviderReducer = Pick<UserSessionContextState, 'userSession' | 'isReady'>;

const reducerInitialValue: ProviderReducer = {
  isReady: false,
  userSession: null,
};

const UserSessionProvider = ({ children }: UserSessionProviderProps) => {
  const { accounts, inProgress, instance } = useMsal();
  const [providerReducer, setProviderReducer] = useReducer(
    (prev: ProviderReducer, current: Partial<ProviderReducer>) => {
      return {
        ...prev,
        ...current,
      };
    },
    reducerInitialValue,
  );
  const inProgressPrev = usePrevious(inProgress);

  const login = useCallback(() => {
    instance.loginRedirect({ scopes: ['User.Read'] });
  }, [instance]);

  const logout = useCallback(
    () => instance.logout({ account: accounts[0] }),
    [accounts, instance],
  );

  const providerReducerHandler = (
    account: AccountInfo | null,
    roles: string[],
    isRolesReady: boolean,
  ) => {
    setProviderReducer({
      userSession: account
        ? {
            mail: account.username,
            name: account.name ?? '',
            token: account.idToken ?? '',
            roles: roles,
            isRolesReady,
          }
        : null,
      isReady: true,
    });
  };

  useEffect(() => {
    if (inProgress === 'none' && inProgress !== inProgressPrev) {
      const account = accounts[0]?.idToken ? accounts[0] : null;

      sessionStorageService.set('msal.active.token', account?.idToken ?? '');
      if (account?.idToken) {
        getAuthInfo(account).then((response) => {
          const responseRoles = response
            ? response.map((r: string) => r.trim())
            : [''];
          providerReducerHandler(account, responseRoles, true);
        });
      } else {
        providerReducerHandler(account, [], false);
      }

    }
  }, [accounts, inProgress, inProgressPrev, setProviderReducer]);

  const contextValue: UserSessionContextState = useMemo(
    () => ({
      isReady: providerReducer.isReady,
      userSession: providerReducer.userSession,
      login,
      logout,
    }),
    [providerReducer.isReady, providerReducer.userSession, login, logout],
  );

  return (
    <UserSessionContext.Provider value={contextValue}>
      {children}
    </UserSessionContext.Provider>
  );
};

export default UserSessionProvider;
